/**
 * Created by Andrey Popov on 19.12.2024
 */

const MergeComponent = function (unit) {
    UnitComponent.call(this, unit);

    this.highlighted = [];
};

MergeComponent.prototype = Object.create(UnitComponent.prototype);
MergeComponent.prototype.constructor = MergeComponent;

MergeComponent.prototype.destructor = function () {
    this.unhighlightUnits();
};

MergeComponent.prototype.isMergeable = function (otherUnit) {
    if (this.unit.findComponent(ScissorsComponent) && otherUnit.findComponent(ScissorsComponent)) {
        return true;
    }

    if (this.unit.getData().unmergeable || this.unit.isLast()) {
        return false;
    }

    return otherUnit.code === this.unit.code && otherUnit.stage === this.unit.stage;
};

MergeComponent.prototype.handleDragMove = function (rawX, rawY) {
    const unit = this.findAffectedUnit(rawX, rawY);

    if (unit) {
        this.highlightMerge(unit);
    } else {
        this.unhighlightUnits();
    }

    return Boolean(unit);
};

MergeComponent.prototype.handleDragEnd = function (rawX, rawY, cancelled) {
    this.unhighlightUnits();

    const affected = this.findAffectedUnit(rawX, rawY);
    if (affected && !cancelled) {
        this.merge([affected]);
        return true;
    }
};

MergeComponent.prototype.highlightMerge = function (otherUnit) {
    const highlighted = [otherUnit.findComponent(MergeComponent)];

    for (let i = 0; i < highlighted.length; i++) {
        const comp = highlighted[i];
        const current = this.highlighted[i];
        if (current === comp) {
            continue;
        }

        if (current) {
            current.stopHighlight();
        }

        comp.startMergeHighlight();
    }

    this.highlighted = highlighted;
};

MergeComponent.prototype.highlightHint = function (otherUnit, options) {
    const highlighted = [this, otherUnit.findComponent(MergeComponent)];

    this.unhighlightUnits();

    const { onCycle, ...viewOptions } = options || {};

    this.highlighted = highlighted;
    this.highlighted[0].startHintHighlight(this.highlighted[1].unit, { ...viewOptions, onCycle });
    this.highlighted[1].startHintHighlight(this.highlighted[0].unit, viewOptions);
};

MergeComponent.prototype.unhighlightUnits = function () {
    const highlighted = this.highlighted;
    this.highlighted = [];

    highlighted.forEach((comp) => comp.stopHighlight());
};

MergeComponent.prototype.startMergeHighlight = function () {
    if (this.view) {
        this.view.startAnimateMerge();
    }
};

MergeComponent.prototype.startHintHighlight = function (targetUnit, options) {
    const grounded = this.unit.findComponent(Grounded);
    if (grounded) {
        grounded.onStartMergeHint();
    }
    if (this.view) {
        this.view.startAnimateHint(targetUnit, options);
    }
};

MergeComponent.prototype.stopHighlight = function () {
    if (this.view) {
        this.view.stopHighlight(() => {
            const grounded = this.unit.findComponent(Grounded);
            if (grounded) {
                grounded.onStopMergeHint();
            }
        });
    }
};

MergeComponent.prototype.highlightMergeHint = function (affected, options) {
    const cell = affected[1];
    if (cell) {
        const unit = Merge2.current.field.getUnit(cell.x, cell.y);
        if (unit) {
            this.highlightHint(unit, options);
        }
    }
};

MergeComponent.prototype.isAffectedUnit = function (unit) {
    return this.unit.isMergeable(unit) && unit.isMergeable(this.unit);
};

MergeComponent.prototype.merge = function (affectedUnits) {
    affectedUnits.push(this.unit);
    const silent = Merge2.current.autoPlay && Merge2.current.autoPlay.isRunning;
    const componentView = this.view;

    const pivotUnit = affectedUnits[0];
    const mergeBonus = pivotUnit.findComponent(MergeComponent).mergeBonus(affectedUnits.length);
    const scissorsComponent = this.unit.findComponent(ScissorsComponent);
    let createdUnitInfo;
    if (scissorsComponent) {
        createdUnitInfo = scissorsComponent.mergeUnit(affectedUnits);
    } else {
        createdUnitInfo = pivotUnit.findComponent(MergeComponent).mergeUnit();
    }

    affectedUnits.forEach((unit) => {
        componentView.removeMergeUnit(unit, silent);
        unit.remove(true);
    });

    const newUnits = [];

    for (let i = 0; i < mergeBonus.bonus + mergeBonus.keepLast; i++) {
        const newUnitInfo = {
            code: pivotUnit.code,
            stage: pivotUnit.stage
        };

        if (i < mergeBonus.bonus) {
            Object.assign(newUnitInfo, createdUnitInfo);
        }

        const pos = affectedUnits[i];
        if (!pos) {
            cleverapps.throwAsync("too many units created after merge");
            break;
        }

        const slot = Merge2.current.field.findEmptySlot(pos.x, pos.y, newUnitInfo);
        if (!slot) {
            cleverapps.throwAsync("no slot found to place unit after merge");
            break;
        }

        const newUnit = new Unit(newUnitInfo);
        newUnit.setPosition(slot.x, slot.y);
        newUnits.push(newUnit);

        Merge2.current.field.addUnit(slot.x, slot.y, newUnit);
        Merge2.current.field.onAddUnit(slot.x, slot.y, newUnit);
        Merge2.current.field.selectUnit(newUnit);
    }

    const bonusSpawn = this.getBonusSpawn(pivotUnit);
    if (bonusSpawn) {
        Merge2.current.spawn([bonusSpawn], newUnits[0], { silent, effect: "merge.spawn.bonus_star" });
        cleverapps.playSession.logEvent(cleverapps.EVENTS.MERGE_BONUS);
    }

    const finish = cleverapps.wait(newUnits.length, () => {
        Field.mapEvent(Field.MERGE, { affected: affectedUnits });
        cleverapps.tutorial.trigger("merge", affectedUnits);
        cleverapps.eventBus.trigger(`SPAWN_AFTER_MERGE_${newUnits[0].code}_${newUnits[0].stage}`);
        Merge2.current.counter.trigger();
    });

    newUnits.forEach((unit, index) => {
        unit.setBlock(true);

        const prize = unit.getCreatePrize();
        let rewards;
        if (prize) {
            rewards = new RewardsList(prize, { event: cleverapps.EVENTS.EARN.OTHER });
            rewards.receiveRewards();
        }

        Merge2.current.field.onUnitAvailable(unit);

        const onMerge = () => {
            unit.setBlock(false);

            Merge2.current.field.onUnitFresh(unit);

            if (rewards) {
                rewards.collectRewardsAnimation(unit, {
                    flyingAnimation: Reward.NO_ANIMATION,
                    delay: 700
                });
            }

            if (index < mergeBonus.bonus) {
                const data = { unit, affected: affectedUnits };
                Field.mapEvent(Field.SPAWN, data);
                cleverapps.tutorial.trigger(Field.SPAWN);
            }

            finish();
        };

        if (silent) {
            onMerge();
        } else {
            componentView.spawnMergeUnit(unit, pivotUnit, onMerge);
        }
    });

    this.suggestOffer(newUnits);
};

MergeComponent.prototype.getBonusSpawn = function (mergedUnit) {
    const type = Families[mergedUnit.code].type;

    if ((mergedUnit.stage >= 6 && type.includes("resource")) || (mergedUnit.stage >= 5 && type.includes("generator"))) {
        return { code: "qq", stage: 0 };
    }
};

MergeComponent.prototype.mergeUnit = function () {
    const nextStage = this.unit.stage + 1;

    if (!Families[this.unit.code].units[nextStage]) {
        return undefined;
    }

    return {
        code: this.unit.code,
        stage: nextStage
    };
};

MergeComponent.prototype.suggestOffer = function (newUnits) {
    if (Merge2.current.autoPlay && Merge2.current.autoPlay.isRunning) {
        return;
    }

    if (Math.random() > ThirdElement.PROBABILITY[cleverapps.paymentsHistory.classify()] || !ThirdElement.IsAvailable(newUnits[0])) {
        return;
    }

    Merge2.current.thirdElementPlanner.planNext({
        delay: 2200,
        target: {
            code: newUnits[0].code,
            stage: newUnits[0].stage,
            x: newUnits[0].x,
            y: newUnits[0].y
        }
    });
};

MergeComponent.prototype.mergeBonus = function () {
    return {
        bonus: 1,
        keepLast: 0,
        next: Infinity
    };
};